import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import { get, getRulesFromState, haveRulesChanged } from 'ctm-util-functions';
import { resultsConfigSelector } from 'ctm-selectors';

import { Button } from '../../../button/button';

import * as actions from '../../actions';

import styles from './display_mode_toggle.css';

/**
 * Display Mode Toggle Component
 * @extends Component
 * @desc A component used to toggle displayMode of results via tab buttons.
 * @returns {DisplayModeToggle} a Display Mode Toggle Component
 */
class _DisplayModeToggle extends Component {

    /**
     * Don't re-render unless rules have changed
     * @param {Object} nextProps - The incoming set of props
     * @param {Object} nextState - The incoming component state
     * @returns {Boolean} - True if the component should re-render, false otherwise
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        const { displayMode } = this.props;
        return haveRulesChanged(nextProps, this.props) || nextProps.displayMode !== displayMode;
    };

    /**
     * Handles clicking on an button
     * @param {Event} event The elements event object
     * @param {String} view The view of the result
     */
    handleClick = (event, view) => {
        const { displayMode, dispatch, eventProps } = this.props;
        if (displayMode !== view) {
            dispatch(actions[eventProps.actionName](view));
            if (eventProps.setPreviousMode) {
                dispatch({ type: 'SET_RESULTS_CONFIG_VALUE', key: 'previousDisplayMode', value: view });
            }
        }
    };

    /**
     * Builds each of the Tab buttons from contentProps
     * @returns {Array} An array of tab buttons.
     */
    getChildren = () => {
        const { contentProps, displayMode } = this.props;
        return contentProps.values.map((obj, index) => (
            <Button
                className={index === 0 ? styles.border_right_none : null}
                label={obj.label}
                icon={obj.icon}
                iconPosition={obj.iconPosition}
                onClick={(event) => this.handleClick(event, obj.view)}
                key={obj.view}
                UIComponent={displayMode === obj.view ? 'btn_feature_3_medium_squared_active' : 'btn_feature_3_medium_squared'}
                variant={displayMode === obj.view ? get(this.props, 'visualProps.active') : get(this.props, 'visualProps.inactive')}
            />
        )
        );
    };

    /**
     * Render the tab buttons
     * @returns {Object} - Returns the Display Mode Toggle Component
     */
    render = () => {
        const { contentProps } = this.props;
        if (!get(this.props, 'rules.visibility.result', true)) {
            return null;
        }

        let buttons = this.getChildren();
        if (!get(this.props, 'rules.buttonVisibility.result', true)) {
            buttons = buttons.filter((button) => contentProps.toggleButtonVisibility !== button.props.label);
        }

        return (
            // we need the empty dive here to make the whole as a flexItem, @todo the extra div probably should be at the parent?
            <div>
                {buttons}
            </div>
        );
    };

}

_DisplayModeToggle.propTypes = {
    displayMode: PropTypes.string,
    dispatch: PropTypes.func,
    contentProps: PropTypes.shape({
        values: PropTypes.array.isRequired,
        toggleButtonVisibility: PropTypes.string
    }),
    eventProps: PropTypes.shape({
        actionName: PropTypes.string.isRequired,
        setPreviousMode: PropTypes.bool.isRequired
    }).isRequired
};

const mapStateToProps = (state, props) => {
    const displayMode = resultsConfigSelector(state).getIn([props.stateProps.displayModeStateKey]);
    return {
        displayMode,
        rules: getRulesFromState(state, props)
    };
};

const DisplayModeToggle = connect(mapStateToProps)(_DisplayModeToggle);

export { _DisplayModeToggle, DisplayModeToggle };
